import { Request, Reply } from "../base/models.jsx";
import { MetaDataBin } from "../../utils/bins/models.jsx";

const SERVICE_ID = 8;

const callStates = {
	'hidden': { 
		name: 'Oculta', 
		longName: 'Convocatoria oculta',
		action: 'Ocultar',
		actualDescription: 'Esta convocatoria está oculta, por lo que es invisible para los ciudadanos. Mientras la convocatoria esté oculta podras editar cualquiera de sus campos o incluso cambiar el formulario establecido.',
		description: 'La convocatoria será invisible para los ciudadanos. Mientras la convocatoria esté oculta podras editar cualquiera de sus campos o incluso cambiar el formulario establecido.',
		style: { color: 'white', background: 'rgba(100,100,100,0.8)' } 
	},
	'ready': { 
		name: 'Preparada', 
		longName: 'Convocatoria preparada',
		action: 'Publicar',
		actualDescription: 'Esta convocatoria será visible a partir de la fecha de inicio establecida. Los usuarios se podran postular hasta la fecha de fin de la convocatoria.',
		description: 'La convocatoria será visible para los usuarios a partir de la fecha de inicio establecida. Los usuarios se podran postular hasta la fecha de fin de la convocatoria.',
		style: { color: 'white', background: 'rgba(85,196,85,0.8)' } 
	},
	'in_progress': { 
		name: 'En curso', 
		longName: 'Convocatoria en curso', 
		actualDescription: 'Esta convocatoria está en progreso. Los usuarios podrán postularse hasta la fecha de fin. No se permite editar una convocatoria en progreso, con la excepción de postergar su fecha de fin.',
		style: { color: 'white', background: 'rgba(85,196,85,0.8)' } 
	}, 
	'waiting_results': { 
		name: 'Sin resultados', 
		longName: 'Convocatoria finalizada sin resultados', 
		actualDescription: 'Esta convocatoria ya finalizó, pero todavía no cuenta con resultados publicados.',
		style: { color: 'white', background: 'rgba(165,92,238,0.8)' } 
	},
	'with_results': { 
		name: 'Finalizada', 
		longName: 'Convocatoria finalizada con resultados', 
		actualDescription: 'Esta convocatoria ya finalizó y sus resultados son accesibles por los usuarios que se postularon.',
		style: { color: 'white', background: 'rgba(92,165,238,0.8)' } 
	},
	'suspended': { 
		name: 'Suspendida', 
		longName: 'Convocatoria suspendida',
		action: 'Suspender',
		actualDescription: 'Esta convocatoria está suspendida. Los usuarios no podrán postularse ni editar sus postulaciones hasta que se reanude la convocatoria.',
		description: 'La convocatoria quedará suspendida y no aceptara nuevas postulaciones hasta que se reanude.',
		style: { color: 'white', background: 'rgba(238,165,92,0.8)' } 
	},
	'deleted': { 
		name: 'Eliminada', 
		longName: 'Convocatoria eliminada', 
		action: 'Eliminar',
		actualDescription: 'Esta convocatoria está eliminada y no se puede reanudar.',
		description: 'La convocatoria quedará eliminada y no podrá reanudarse.',
		style: { color: 'white', background: 'rgba(238,92,92,0.8)' } 
	},
	getByCode: (state) => {
		if (state === 1)
			return callStates['hidden'];
		else if (state === 2)
			return callStates['ready'];
		else if (state === 3)
			return callStates['suspended'];
		else if (state === 4)
			return callStates['deleted'];
	}
}

const applicationStates = ["Abierto", "Enviado", "Cancelado", "Recepcionado", "Rechazado"]
const applicationReplyCodes = ["Reabierto", "Recepcionada", "Rechazado"]

class Call {

	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined)
			jsonData = {};

		this.id = jsonData.id;
		this.name = jsonData.name;
		this.description = jsonData.description;
		this.configurationId = jsonData.configurationId;
		this.startDate = jsonData.startDate;
		this.endDate = jsonData.endDate;
		this.state = jsonData.state;
		this.applications = jsonData.applications;
		
		if (jsonData.configuration !== undefined && jsonData.configuration != null)
			this.configuration = new Configuration(jsonData.configuration);

		if (jsonData.result !== undefined && jsonData.result != null)
			this.result = new CallResult(jsonData.result);
	}

	readyToCreate() {
		return (this.name != null && this.name !== undefined && this.name.length > 0 && 
			this.description != null && this.description !== undefined && this.description.length > 0 && 
			this.configurationId != null && this.configurationId !== undefined && 
			this.startDate != null && this.startDate !== undefined && 
			this.endDate != null && this.endDate !== undefined &&
			this.startDate < this.endDate);
	}

	hasResults() {
		return this.result != null && this.result !== undefined;
	}

	canLoadResults() {
		let now = Date.now();
		return this.state === 2 && now > this.endDate;
	}

	canChangeStartDate() {
		return !this.hasResults() && (this.state === 1 || this.canGoHidden());
	}

	canChangeEndDate() {
		return !this.hasResults() && this.state !== 4;
	}

	minStartDate() {
		let min = Date.now();
		if (this.startDate < min)
			min = this.startDate;
		return min;
	}

	canAnticipateEndDate() {
		return !this.hasResults() && (this.state === 1 || (this.state !== 3 && (Date.now()) < this.startDate));
	}

	minEndDate() {
		if (!this.canChangeEndDate())
			return this.endDate;

		let min = Date.now();
		if (this.startDate > min || this.canAnticipateEndDate())
			min = this.startDate;
		if (this.endDate > min && !this.canAnticipateEndDate())
			min = this.endDate;
		return min;
	}

	canChangeConfiguration() {
		return !this.hasResults() && (this.state === 1 || this.canGoHidden());
	}

	canGoHidden() {
		return !this.hasResults() && (this.state === 2 && (Date.now()) < this.startDate);
	}

	canGoReady() {
		return !this.hasResults() && (this.state === 1 || this.state === 3);
	}

	canGoSuspended() {
		let now = Date.now();
		return !this.hasResults() && this.state === 2 && now > this.startDate && now < this.endDate;
	}

	canGoDeleted() {
		return !this.hasResults() && this.state !== 4;
	}

	getStateDescription() {
		if (this.state === 1) {
			return callStates['hidden'];
		} else if (this.state === 2) {
			let now = Date.now()
			if (now > this.endDate) {
				if (this.result == null || this.result === undefined)
					return callStates['waiting_results'];
				else
					return callStates['with_results'];
			} else if (now > this.startDate) {
				return callStates['in_progress'];
			} else {
				return callStates['ready'];
			}
		} else if (this.state === 3) {
			return callStates['suspended'];
		} else if (this.state === 4) {
			return callStates['deleted'];
		}
	}

}

class CallResult {

	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined)
			jsonData = {};

		this.id = jsonData.id;
		this.time = jsonData.time;

		if (jsonData.files !== undefined && jsonData.files != null) {
			this.files = [];
			for (var index = 0; index < jsonData.files.length; index++) {
				this.addFile(new ResultFile(jsonData.files[index]));
			}
		} else {
			this.files = null;
		}
	}

	addFile(file) {
		this.files.push(file);
	}

}

class ResultFile {

	constructor(jsonData) {
		this.id = jsonData.id;
		this.resultId = jsonData.resultId;
		this.binId = jsonData.binId;
		this.bin = new MetaDataBin(jsonData.bin);
	}

}

class Configuration {

	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined)
			jsonData = {};

		this.id = jsonData.id;
		this.name = jsonData.name;
		this.description = jsonData.description;
		this.assigned = jsonData.assigned;
		this.usedBy = jsonData.usedBy;

		if (jsonData.inputs !== undefined && jsonData.inputs != null) {
			this.inputs = [];
			for (var index = 0; index < jsonData.inputs.length; index++) {
				this.addInput(new Input(jsonData.inputs[index]));
			}
		} else {
			this.inputs = null;
		}
	

	}

	readyToCreate() {
		return (this.name != null && this.name !== undefined && this.name.length > 0 && 
			this.description != null && this.description !== undefined && this.description.length > 0);
	}

	addInput(input) {
		this.inputs.push(input);
	}

	removeInput(input) {
		this.inputs = this.inputs.filter(item => item.id !== input.id);
	}

	isEditable() {
		return this.usedBy === 0;
	}

}

class Input {

	constructor(jsonData) {
		this.id = jsonData.id;
		this.configurationId = jsonData.configurationId;
		this.position = jsonData.position;
		this.name = jsonData.name;
		this.description = jsonData.description;
		this.typeId = jsonData.typeId;
		this.auxiliaryFiles = [];
		if (jsonData.auxiliaryFiles != null) {
			for (var index = 0; index < jsonData.auxiliaryFiles.length; index++) {
				this.addAuxiliaryFile(new AuxiliaryFile(jsonData.auxiliaryFiles[index]));
			}
		}
	}

	addAuxiliaryFile(auxiliaryFile) {
		this.auxiliaryFiles.push(auxiliaryFile);
	}

	removeAuxiliaryFile(auxiliaryFile) {
		this.auxiliaryFiles = this.auxiliaryFiles.filter(item => item.id !== auxiliaryFile.id);
	}

}

class AuxiliaryFile {

	constructor(jsonData) {
		this.id = jsonData.id;
		this.inputId = jsonData.inputId;
		this.binId = jsonData.binId;
		this.bin = new MetaDataBin(jsonData.bin);
	}

}


class Application extends Request {

	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined) {
			jsonData = {};
			super(jsonData);
			this.creation();
		} else {
			super(jsonData);
		}

		this.callId = jsonData.callId;
		this.extensionDate = jsonData.extensionDate;

		if (jsonData.call != null)
			this.call = new Call(jsonData.call);


		this.contents = []
		if (jsonData.contents) {
			for (var index = 0; index < jsonData.contents.length; index++) {
				this.addContent(new ApplicationContent(jsonData.contents[index]));
			}
		}

		this.replies = []
		if (jsonData.replies) {
			for (var indexReply = 0; indexReply < jsonData.replies.length; indexReply++) {
				this.addReply(new ApplicationReply(jsonData.replies[indexReply]));
			}
		}
	}	

	addContent(content) {
		this.contents.push(content);
	}

	addReply(reply) {
		this.replies.push(reply);
	}

	creation() {
		super.creation();
		this.serviceId = SERVICE_ID;
	}

	canSend() {
    	if (this.state !== 0)
      		return false;

   		if (this.call.state !== 2)
   			return false;

  		let now = Date.now();
	    return !(now > this.call.endDate && (this.extensionDate == null || now > this.extensionDate));
  	}

  	canModify() {
   		return this.canSend();
  	}

}

class ApplicationReply extends Reply {
	
	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined) {
			jsonData = {};
		}

		super(jsonData);

		this.extensionDate = jsonData.extensionDate;
		this.message = jsonData.message;
	}

	creation() {
		super.creation();
		this.serviceId = SERVICE_ID;
	}

}
class ApplicationContent {

	constructor(jsonData) {
		if (jsonData == null || jsonData === undefined)
			jsonData = {};
	
		this.id = jsonData.id;
		this.requestId = jsonData.requestId;
		this.inputId = jsonData.inputId;
		this.lastModification = jsonData.lastModification; 

		this.files = [];
		for (var index = 0; index < jsonData.files.length; index++) {
			this.addFile(new ApplicationContentFile(jsonData.files[index]));
		}
	}	

	addFile(file) {
		this.files.push(file);
	}

	removeFile(file) {
		this.files = this.files.filter(item => item.id !== file.id);
	}

  	
}

class ApplicationContentFile {

	constructor(jsonData) {
		this.id = jsonData.id;
		this.contentId = jsonData.contentId;
		this.binId = jsonData.binId;
		this.bin = new MetaDataBin(jsonData.bin);
	}

}

export { Application, ApplicationContent, ApplicationReply, applicationStates, applicationReplyCodes, Call, CallResult, callStates, Configuration, Input, AuxiliaryFile };